import _split from "lodash/split"
import _toNumber from "lodash/toNumber"
import _toSafeInteger from "lodash/toSafeInteger"

const COMMA_GLOBAL_REGEX = /,/g
const COMMA_REGEX = /,/
const HTML_TAGS_REGEX = /<[^>]+>/g
const PARTIAL_FLOAT_INPUT_REGEX = /^\d*(?:[.,]\d*)?$/
const COMPLETE_FLOAT_INPUT_REGEX = /^\d+(?:[.,]\d+)?$/

/**
 * Whether a string is a valid in-progress or final decimal input (digits with at most one `.` or `,` separator).
 *
 * @example
 * isAllowedFloatInput("234.23") // true
 * isAllowedFloatInput("432,234") // true
 * isAllowedFloatInput("sdfg33") // false
 * isAllowedFloatInput("234,234,234") // false
 * isAllowedFloatInput("2435.2,34") // false
 */
export function isAllowedFloatInput(value: string): boolean {
	if (value === "") return true
	if (!PARTIAL_FLOAT_INPUT_REGEX.test(value)) return false
	return !(value.includes(".") && value.includes(","))
}

/** True when the string is a finished decimal (not an in-progress fragment like `234.`). */
export function isCompleteFloatInput(value: string): boolean {
	if (!value || !isAllowedFloatInput(value)) return false
	return COMPLETE_FLOAT_INPUT_REGEX.test(value)
}

/**
 * Converts a string or number to a decimal number.
 * Replaces commas with dots and properly handles decimal separators.
 *
 * @param {string | number} input - The value to convert to a number
 * @returns {number} The converted number
 *
 * @example
 * toNumber("123,45") // 123.45
 * toNumber("123.45") // 123.45
 * toNumber(123) // 123
 */
export function toNumber(input: string | number): number {
	return _toNumber(_split(`${input}`.replace(COMMA_GLOBAL_REGEX, "."), ".", 2).join("."))
}

/**
 * Converts a string to a safe integer.
 * Replaces comma with dot and truncates any decimals.
 *
 * @param {string} input - The string to convert to an integer
 * @returns {number} The safely converted integer
 *
 * @example
 * toSafeInt("123,45") // 123
 * toSafeInt("999999999999999999") // 9007199254740991 (Number.MAX_SAFE_INTEGER)
 */
export function toSafeInt(input: string | number): number {
	return _toSafeInteger(`${input}`.replace(COMMA_REGEX, "."))
}

/**
 * Converts a value to a boolean.
 * Returns true if the value is "1" or "true", otherwise false.
 *
 * @param {string | boolean | number | null} [val] - The value to convert to boolean
 * @returns {boolean} The resulting boolean value
 *
 * @example
 * toBoolean("1") // true
 * toBoolean("true") // true
 * toBoolean("0") // false
 * toBoolean(null) // false
 */
export function toBoolean(val?: string | boolean | number | null): boolean {
	const cast = (val || 0).toString()
	if (cast === "1") return true
	if (cast === "true") return true

	return false
}

/**
 * Combines the prefix and phone number into a formatted string.
 *
 * @param {string} prefix - The phone prefix (e.g. "+39")
 * @param {string} phone - The phone number
 * @returns {string} The formatted string with prefix and number separated by a space
 *
 * @example
 * phoneChunksToString("+39", "1234567890") // "+39 1234567890"
 */
export function phoneChunksToString(prefix: string, phone: string): string {
	return `${prefix} ${phone}`
}

/**
 * Splits a phone string into prefix and number.
 * Returns null for both values if the string is empty.
 *
 * @param {string} phone - The complete phone string (e.g. "+39 1234567890")
 * @returns {{ phone: string | null; prefix: string | null }} An object containing the separated prefix and number
 *
 * @example
 * phoneStringToChunks("+39 1234567890") // { prefix: "+39", phone: "1234567890" }
 * phoneStringToChunks("") // { prefix: null, phone: null }
 * phoneStringToChunks("1234567890") // { prefix: null, phone: "1234567890" }
 */
export function phoneStringToChunks(phone: string): { phone: string | null; prefix: string | null } {
	return {
		phone: (phone?.includes(" ") ? _split(phone, " ")[1] : phone) || null,
		prefix: phone?.includes(" ") ? _split(phone, " ")[0] || null : null
	}
}

/**
 * Removes all HTML tags from a string.
 *
 * @param value - The input string that may contain HTML tags.
 * @returns The cleaned string without any HTML tags.
 *
 * @example
 * stripHtmlTags('In <strong>279</strong> days');
 *  => 'In 279 days'
 */
export function stripHtmlTags(value: string): string {
	return value.replace(HTML_TAGS_REGEX, "")
}
